import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import {
  isInsightsGroupActive,
  isStandalonePathActive,
  type StandaloneNavItem
} from './standaloneNavItems';

const NAV_GROUP_LABEL = 'Specialized Dashboards';

type InsightsNavDropdownProps = {
  variant: 'desktop' | 'mobile';
  items: StandaloneNavItem[];
  pathname: string;
  onNavigate: (path: string) => void;
};

export const InsightsNavDropdown = ({
  variant,
  items,
  pathname,
  onNavigate
}: InsightsNavDropdownProps) => {
  if (items.length === 0) return null;

  if (variant === 'mobile') {
    return <MobileInsights items={items} pathname={pathname} onNavigate={onNavigate} />;
  }

  return <DesktopInsights items={items} pathname={pathname} onNavigate={onNavigate} />;
};

const DesktopInsights = ({
  items,
  pathname,
  onNavigate
}: Omit<InsightsNavDropdownProps, 'variant'>) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const groupActive = isInsightsGroupActive(pathname, items);

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 240;
    const gap = 6;
    let left = rect.left;
    // Keep menu on-screen when Insights is the last nav item.
    left = Math.min(left, window.innerWidth - menuWidth - 12);
    left = Math.max(12, left);
    setMenuPos({ top: rect.bottom + gap, left });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <DesktopRoot ref={rootRef}>
      <DesktopTrigger
        ref={triggerRef}
        type="button"
        className={groupActive ? 'selected' : ''}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={NAV_GROUP_LABEL}
        onClick={() => setOpen((v) => !v)}
      >
        <DesktopIcon aria-hidden="true">
          <InsightsGlyph isSelected={groupActive} />
        </DesktopIcon>
        <DesktopLabel>{NAV_GROUP_LABEL}</DesktopLabel>
        <Caret $open={open} aria-hidden="true" />
      </DesktopTrigger>
      {open &&
        createPortal(
          <DesktopMenu
            ref={menuRef}
            role="menu"
            aria-label={`${NAV_GROUP_LABEL} menu`}
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {items.map((item) => {
              const active = isStandalonePathActive(pathname, item.path);
              return (
                <DesktopItem
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className={active ? 'active' : ''}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => {
                    onNavigate(item.path);
                    setOpen(false);
                  }}
                >
                  <span>{item.label}</span>
                </DesktopItem>
              );
            })}
          </DesktopMenu>,
          document.body
        )}
    </DesktopRoot>
  );
};

const MobileInsights = ({
  items,
  pathname,
  onNavigate
}: Omit<InsightsNavDropdownProps, 'variant'>) => {
  const groupActive = isInsightsGroupActive(pathname, items);
  const [open, setOpen] = useState(groupActive);

  useEffect(() => {
    if (groupActive) setOpen(true);
  }, [groupActive]);

  return (
    <MobileRoot>
      <MobileHeader
        type="button"
        className={groupActive ? 'active' : ''}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <div className="nav-item-container">
          <div className="nav-item-content">
            <div className="nav-item-icon">
              <InsightsGlyph isSelected={groupActive} />
            </div>
            <div className="nav-item-text">
              <div className="nav-item-title">{NAV_GROUP_LABEL}</div>
            </div>
          </div>
          <MobileCaret $open={open} aria-hidden="true" />
        </div>
      </MobileHeader>
      {open && (
        <MobileList>
          {items.map((item) => {
            const active = isStandalonePathActive(pathname, item.path);
            return (
              <MobileItem
                key={item.id}
                type="button"
                className={active ? 'active' : ''}
                aria-current={active ? 'page' : undefined}
                onClick={() => onNavigate(item.path)}
              >
                <span>{item.label}</span>
                {active && <div className="nav-item-indicator" />}
              </MobileItem>
            );
          })}
        </MobileList>
      )}
    </MobileRoot>
  );
};

const InsightsGlyph = ({ isSelected }: { isSelected: boolean }) => {
  const fill = isSelected ? '#0d8a6a' : 'white';
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7 19V12.5M14 19V8M21 19v-5.5"
        stroke={fill}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M5.5 21.5h17"
        stroke={fill}
        strokeWidth="1.8"
        strokeLinecap="round"
        opacity="0.55"
      />
    </svg>
  );
};

const DesktopRoot = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: stretch;
`;

const DesktopTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 14px;
  background: transparent;
  border: none;
  border-right: 1px solid rgb(73, 76, 82);
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #c9c9c9;
  }

  &.selected {
    color: #0d8a6a;
  }
`;

const DesktopIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const DesktopLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
`;

const Caret = styled.i<{ $open: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 2px;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentColor;
  opacity: 0.75;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.15s ease;
`;

const DesktopMenu = styled.div`
  position: fixed;
  z-index: 2000;
  min-width: 240px;
  padding: 6px;
  border-radius: 10px;
  background: #1f2126;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
`;

const DesktopItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  border: 0;
  background: transparent;
  color: #f3f4f6;
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  &.active {
    color: #0d8a6a;
    background: rgba(13, 138, 106, 0.12);
  }
`;

const MobileRoot = styled.div`
  width: 100%;
`;

const MobileHeader = styled.button`
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  padding: 0;
  cursor: pointer;
  color: inherit;

  .nav-item-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
`;

const MobileCaret = styled.i<{ $open: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  margin-right: 8px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid currentColor;
  opacity: 0.7;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`;

const MobileList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 0 8px 44px;
  gap: 2px;
`;

const MobileItem = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;

  &.active {
    color: #0d8a6a;
    background: rgba(13, 138, 106, 0.1);
  }

  .nav-item-indicator {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 60%;
    border-radius: 2px;
    background: #0d8a6a;
  }
`;
